
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Sparkles } from "lucide-react";
import { gradientText } from "@/lib/theme-utils";

export const HeroBanner = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [bannerBg, setBannerBg] = useState<number>(1);
  
  // Rotate through banner backgrounds
  useEffect(() => {
    const interval = setInterval(() => {
      setBannerBg(prev => (prev % 3) + 1);
    }, 8000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Update time
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  
  // Fetch featured event
  const { data: featuredEvent } = useQuery({
    queryKey: ["featuredEvent"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select(`
          id,
          name,
          start_time,
          venue_id,
          description,
          image_url,
          venue:venue_id (
            name,
            address
          )
        `)
        .eq('status', 'active')
        .order('start_time', { ascending: true })
        .limit(1)
        .single();
        
      if (error) {
        console.error("Error fetching featured event:", error);
        return null;
      }
      
      return data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Get time-based greeting
  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Rise & Shine';
    if (hour < 17) return 'Afternoon Vibes';
    if (hour < 22) return 'Evening Energy';
    return 'Night Mode: Activated';
  };

  return (
    <div className={`relative overflow-hidden rounded-xl transition-all duration-1500 h-48 md:h-60`}>
      {/* Dynamic gradient background with animation */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-900 via-pink-800 to-purple-900 animate-text-shimmer bg-200%"></div>
      
      {/* Animated lights/circles */}
      <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-purple-600/30 blur-3xl -translate-y-1/2 -translate-x-1/2 animate-pulse-glow"></div>
      <div className="absolute bottom-0 right-0 w-64 h-64 rounded-full bg-pink-600/30 blur-3xl translate-y-1/2 translate-x-1/2 animate-pulse-glow" style={{animationDelay: "1s"}}></div>
      
      {/* Content overlay with glassmorphism */}
      <div className="absolute inset-0 backdrop-blur-sm bg-black/20 p-6 flex flex-col justify-center">
        <div className="relative z-10">
          <p className="text-sm text-white/70 mb-1">{getGreeting()}</p>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2">
            <span className={gradientText()}>Tonight's HQ</span>
          </h1>
          
          {featuredEvent ? (
            <div className="mt-4 bg-black/30 rounded-lg p-3 backdrop-blur-md border border-white/10 max-w-lg animate-fade-in">
              <p className="text-white/80 text-sm">Featured Event:</p>
              <h3 className="text-lg font-bold mb-1">{featuredEvent.name}</h3>
              <div className="flex flex-col gap-1 text-xs text-white/70">
                <div className="flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  <span>
                    {new Date(featuredEvent.start_time).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex items-center">
                  <MapPin className="h-3 w-3 mr-1" />
                  <span>{featuredEvent.venue?.name}</span>
                </div>
              </div>
              <Button 
                size="sm" 
                className="mt-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-500/20"
                asChild
              >
                <Link to={`/dashboard/events/${featuredEvent.id}`}>
                  <Sparkles className="h-3 w-3 mr-1" />
                  Get Tickets
                </Link>
              </Button>
            </div>
          ) : (
            <div className="mt-4 bg-black/30 rounded-lg p-3 backdrop-blur-md border border-white/10 max-w-lg animate-fade-in">
              <h3 className="text-lg font-bold mb-1">The night is yours</h3>
              <p className="text-white/70 text-sm mb-3">Discover events, connect with venues, and make unforgettable memories</p>
              <Button 
                size="sm" 
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-500/20"
                asChild
              >
                <Link to="/dashboard/events">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Explore Events
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
